import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Sparkles, Loader2, Search, PlusCircle, Compass, LayoutGrid, ListTree, Target as TargetIcon, Shield, Upload, ScanLine, Network } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { StatCard, SectionTitle, ScorePill, ComplianceBadge } from "./atoms";

const TABS = [
  { key: "radar",     label: "Opportunity Radar",  Icon: Compass },
  { key: "pipeline",  label: "Pipeline",           Icon: LayoutGrid },
  { key: "list",      label: "Liste des cibles",   Icon: ListTree },
  { key: "map",       label: "Écosystème",         Icon: Network },
  { key: "import",    label: "Importer",           Icon: Upload },
  { key: "ocr",       label: "Scanner",            Icon: ScanLine },
  { key: "strategy",  label: "Ma stratégie",       Icon: TargetIcon },
  { key: "dnc",       label: "Conformité",         Icon: Shield },
];

export default function IntelligenceHub({ children, activeTab = "radar" }) {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [radar, setRadar] = useState({ items: [], strategy_present: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [d, r] = await Promise.all([
          api.get("/cdl/dashboard"),
          api.get("/cdl/radar?limit=6"),
        ]);
        if (!alive) return;
        setDash(d.data);
        setRadar(r.data);
      } catch (e) {
        if (e?.response?.status === 403) {
          toast.error("Accès réservé aux conseillers ALTIORA.");
        } else {
          toast.error("Impossible de charger le tableau de bord.");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const k = dash?.kpis || {};

  return (
    <div data-testid="intelligence-hub-root">
      <TopBar
        title="Intelligence Commerciale"
        subtitle="Repérer, qualifier, prioriser vos opportunités de développement — copilote stratégique ALTIORA."
      />
      <div className="px-6 md:px-10 py-8 md:py-10 max-w-[1400px] mx-auto space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard testId="intel-kpi-total"        label="Cibles totales"  value={loading ? "…" : k.total_targets} />
          <StatCard testId="intel-kpi-allowed"      label="Autorisées"      value={loading ? "…" : k.allowed} hint="conformité OK" />
          <StatCard testId="intel-kpi-to_verify"    label="À vérifier"      value={loading ? "…" : k.to_verify} />
          <StatCard testId="intel-kpi-blocked"      label="Ne pas contacter" value={loading ? "…" : k.blocked} />
          <StatCard testId="intel-kpi-appointments" label="Rendez-vous"     value={loading ? "…" : k.appointments} />
          <StatCard testId="intel-kpi-conv"         label="Taux conversion" value={loading ? "…" : `${k.conversion_rate_pct ?? 0}%`} />
        </div>

        {/* Tabs */}
        <div className="flex gap-2 md:gap-3 border-b border-[#E4E1D8] overflow-x-auto pb-px">
          {TABS.map(({ key, label, Icon }) => (
            <button
              key={key}
              data-testid={`intel-tab-${key}`}
              onClick={() => navigate(`/app/commercial${key === "radar" ? "" : "/" + key}`)}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 -mb-px border-b-2 transition-colors whitespace-nowrap ${
                activeTab === key
                  ? "border-[#0B2545] text-[#0B2545] font-medium"
                  : "border-transparent text-[#5B5B62] hover:text-[#0B2545]"
              }`}
            >
              <Icon className="h-3.5 w-3.5" strokeWidth={1.8} />
              <span className="text-sm">{label}</span>
            </button>
          ))}
        </div>

        {/* Slot */}
        {children ? (
          children
        ) : (
          <RadarView radar={radar} nextActions={dash?.next_actions || []} loading={loading} />
        )}
      </div>
    </div>
  );
}

function RadarView({ radar, nextActions, loading }) {
  const navigate = useNavigate();
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-[#5B5B62]">
        <Loader2 className="h-5 w-5 animate-spin mr-2" strokeWidth={1.8} />
        Analyse des opportunités…
      </div>
    );
  }
  return (
    <>
      {!radar.strategy_present && (
        <div className="rounded-md border border-[#DCC29A] bg-[#FDF7EC] px-5 py-4 text-sm text-[#5C4300]">
          <span className="font-medium">Astuce copilote — </span>
          Déclarez votre stratégie commerciale pour que le radar priorise selon vos cibles.{" "}
          <button
            data-testid="intel-cta-strategy"
            onClick={() => navigate("/app/commercial/strategy")}
            className="underline underline-offset-2 hover:text-[#8A5A00]"
          >
            Configurer ma stratégie
          </button>
        </div>
      )}

      <div>
        <SectionTitle sub="Les cibles les plus prometteuses pour vous, aujourd'hui.">
          Opportunity Radar
        </SectionTitle>
        {radar.items.length === 0 ? (
          <EmptyState
            title="Aucune opportunité pour l'instant"
            hint="Ajoutez une première cible ou importez votre liste."
            cta={{ label: "Ajouter une cible", to: "/app/commercial/list?new=1", Icon: PlusCircle }}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {radar.items.map((t) => (
              <OpportunityCard key={t.target_id} t={t} />
            ))}
          </div>
        )}
      </div>

      {nextActions.length > 0 && (
        <div>
          <SectionTitle sub="Actions concrètes proposées par le copilote.">
            Prochaines actions
          </SectionTitle>
          <div className="rounded-md border border-[#E4E1D8] bg-white divide-y divide-[#F2EFE7]">
            {nextActions.map((a) => (
              <div key={a.target_id} className="flex items-start gap-3 px-5 py-3.5 hover:bg-[#F8F7F3]">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-[#1F2937]">{a.display_name}</div>
                  <div className="text-xs text-[#5B5B62] mt-0.5">
                    {a.next_action_fr || "Complétez la qualification pour obtenir une action recommandée."}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">radar</div>
                  <div className="text-lg font-semibold text-[#0B2545]">{a.radar_score}</div>
                </div>
                <button
                  data-testid={`intel-open-target-${a.target_id}`}
                  onClick={() => navigate(`/app/commercial/list?target=${a.target_id}`)}
                  className="shrink-0 self-center text-xs font-mono uppercase tracking-widest text-[#0B2545] hover:underline"
                >
                  Ouvrir →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function OpportunityCard({ t }) {
  const navigate = useNavigate();
  return (
    <div
      data-testid={`intel-opp-${t.target_id}`}
      className="rounded-md border border-[#E4E1D8] bg-white p-5 hover:border-[#0B2545] transition-colors cursor-pointer"
      onClick={() => navigate(`/app/commercial/list?target=${t.target_id}`)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#1F2937] truncate">{t.display_name}</div>
          <div className="text-[11px] text-[#5B5B62] mt-0.5">
            {[t.organization_name, t.role_or_title].filter(Boolean).join(" · ") || t.target_type}
          </div>
          <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
            {[t.city, t.country].filter(Boolean).join(" · ") || "—"}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#8B8B8F]">radar</div>
          <div className="text-xl font-bold text-[#0B2545]">{t.radar_score}</div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-3">
        <ScorePill label="pertinence" value={t.ai_score} />
        <ScorePill label="impact" value={t.ai_impact} tone="gold" />
        <ScorePill label="probabilité" value={t.ai_probability_success} tone="ghost" />
      </div>

      {t.ai_angle_fr && (
        <p className="mt-4 text-xs text-[#3A3A3F] line-clamp-3">
          <Sparkles className="inline h-3 w-3 mr-1 text-[#DCC29A]" strokeWidth={1.8} />
          {t.ai_angle_fr}
        </p>
      )}

      <div className="mt-4 flex items-center justify-between">
        <ComplianceBadge status={t.compliance_status} />
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
          {t.stage?.replace(/_/g, " ")}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ title, hint, cta }) {
  const navigate = useNavigate();
  const Icon = cta?.Icon || Search;
  return (
    <div className="rounded-md border border-dashed border-[#E4E1D8] bg-white/40 px-6 py-16 text-center">
      <div className="mx-auto h-10 w-10 rounded-full bg-[#F2EFE7] flex items-center justify-center mb-3">
        <Icon className="h-4 w-4 text-[#0B2545]" strokeWidth={1.6} />
      </div>
      <div className="text-sm font-medium text-[#0B2545]">{title}</div>
      {hint && <div className="mt-1 text-xs text-[#5B5B62]">{hint}</div>}
      {cta && (
        <button
          data-testid="intel-empty-cta"
          onClick={() => navigate(cta.to)}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#DCC29A] text-white font-medium px-4 py-2 text-sm transition-colors"
        >
          {cta.label}
        </button>
      )}
    </div>
  );
}
